<script lang="ts" setup>
	import useProductActivationApi from '~/services/apis/product-activation'
	import { useResourcesApi } from '~/services/apis/resources'
	import type { NeedToKnowProps } from '~/components/Shared/need-to-know.vue'
	import type { BatchUploadSteps } from '~/components/Uploads/batch.vue'
	import StringMethods from '~/utils/character'
	import { filterObject } from '~/utils/functions'
	import type { AddToPlanProps } from '~/pages/manage-custodian/[purchase_id]/add/new.vue'

	type CustodianContext = {
		sn?: boolean
		isUsed?: boolean
		temp_id: string
		vehicle_owner?: string
		vehicle_type: DynamicTableRecord
		vehicle_owner_name: DynamicTableRecord
		manufacturer_id: DynamicTableRecord
		employee_id: DynamicTableRecord
		vehicle_value: DynamicTableRecord
		vehicle_model?: DynamicTableRecord
		premium?: DynamicTableRecord
		plate_number?: DynamicTableRecord
		first_name?: DynamicTableRecord
		last_name?: DynamicTableRecord
		email?: DynamicTableRecord
		phone_number?: DynamicTableRecord
		isActive?: boolean
		editable?: boolean
		done?: boolean
	}

	const props = defineProps<AddToPlanProps>()

	const { convertNumberToText } = StringMethods()
	const { internationalNoFormat, StringToNumber } = useCurrency()
	const { addAutoCustodian } = useProductActivationApi()
	const { urlQuery } = useUtils()
	const { validatePlateNumber } = useResourcesApi()
	const MINIMUM_VEHICLE_VALUE = 500000

	const emits = defineEmits<{
		(event: 'update:form', payload: unknown[]): void
		(event: 'update:viewStaff', payload: boolean): void
		// (event: 'update:staffList', payload: boolean): void
		(event: 'done', response: NeedToKnowProps): void
	}>()

	const batchUploadGuide: BatchUploadSteps = {
		'Start here': {
			desc: 'Download the CSV/ XLS file template below',
			key: 'start'
		},
		'Fill Details': {
			desc: 'Fill in the CSV/ XLS file you just downloaded with your staff information which should include <span class="text-gray-600 font-medium">First name,Last name,Email address, Phone number, and the Total Staff Dependants</span>.  You can use <a href="https://docs.google.com/spreadsheets" target="_blank" class="text-blue-500 font-medium">Google sheet</a> to edit the template or save it to your desktop or any folder on your PC and edit with excel software. See sample below:',
			key: 'details'
		},
		'Upload saved file ': {
			desc: 'Upload or drag & drop the saved CSV/ XLS file you just filled on your PC.',
			key: 'upload'
		}
	}
	const formToSubmit = computed(() => props.form)
	const fetchingSlots = shallowRef(false)
	const isSubmitting = shallowRef(false)
	const vehicleManufacturer = ref<SelectOptionContext>([])

	const planInformation = computed(() => {
		return props.purchaseInfo ?? null
	})
	const { numberOfStaffAdded, totalStaffPendingSlot, categoryType } = usePurchaseInfo({ planInformation })

	const totalStaffAdded = computed(() => numberOfStaffAdded.value + Number(props.form?.length))
	const totalStaffPending = computed(() =>
		totalStaffPendingSlot.value > 0 ? totalStaffPendingSlot.value - Number(props.form?.length) : 0
	)

	const needToKnow = computed<NeedToKnowProps>(() => [
		{
			description: ` You have added policy holders to  ${numToTxt(totalStaffAdded.value)} (${
				totalStaffAdded.value ?? 0
			})  of your registered vehicles.`,

			icon: {
				dir: 'fill',
				name: 'car',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600 capitalize'
		},
		{
			description:
				'Policy holders that have been added to each vehicle will receive an email for activation and inspection.',
			icon: {
				dir: 'fill',
				name: 'people',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600 capitalize'
		},
		{
			description: ` ${numToTxt(totalStaffPending.value)} (${
				totalStaffPending.value ?? 0
			}) of your vehicles do not have drivers, you will need to activate ${
				(categoryType.value?.isComprehensive && 'and inspect them yourself.') || '.'
			}`,
			icon: {
				dir: 'other',
				name: 'search',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600 capitalize',
			wrapperClass: {
				hidden: !totalStaffPending.value
			}
		},
		{
			description: 'Vehicle coverage must be inspected to be activated.',
			icon: {
				dir: 'fill',
				name: 'question-2',
				pathClass: 'fill-warning-500',
				class: 'bg-warning-50'
			},
			wrapperClass: `${(categoryType.value?.isThirdParty && '!hidden') || ''} `,
			descClass: 'text-warning-500 italic'
		}
	])
	const { User } = reactor(useUser())
	const tableColumns = computed<TableColumn[]>(() => {
		const staffColumn: TableColumn[] = !props.uploadFromEmployees
			? [
					{
						key: 'first_name',
						dataIndex: 'first_name',
						title: 'First Name',
						class: 'min-w-[20%]'
					},
					{
						key: 'last_name',
						dataIndex: 'last_name',
						title: 'Last Name',
						class: 'min-w-[20%]'
					},
					{
						key: 'email',
						dataIndex: 'email',
						title: 'Email Address',
						class: 'min-w-[20%]'
					},
					{
						key: 'phone_number',
						dataIndex: 'phone',
						title: 'Phone Number',
						class: 'min-w-[20%]'
					}
				]
			: [
					{
						key: 'employee_id',
						dataIndex: 'employee_id',
						title: props.viewStaff ? 'Staff' : 'Staff List',
						className: 'min-w-[200px]'
					}
				]
		return [
			{ key: 'sn', title: 'S/N', class: 'text-center' },
			{ key: 'vehicle_type', title: 'Vehicle Type', class: 'min-w-[20%]' },
			{ key: 'manufacturer_id', title: 'Manufacturer', class: 'min-w-[20%]' },
			{
				key: 'vehicle_model',
				title: 'Model',
				className: ` ${(!categoryType.value?.isComprehensive && 'hidden') || ''} min-w-[100px]`,
				class: `min-w-[20%] ${(!categoryType.value?.isComprehensive && 'hidden') || ''} `
			},
			{
				key: 'vehicle_value',
				title: 'Value',
				className: 'min-w-[120px]'
			},
			{ key: 'vehicle_owner_name', title: 'Vehicle Owner Name', className: 'min-w-[20%]' },
			{
				key: 'plate_number',
				dataIndex: 'plate_number',
				title: 'Plate Number',
				class: 'min-w-[20%]'
			},
			...staffColumn,
			{ key: 'action', title: 'Action' }
		]
	})
	function numToTxt(payload: number) {
		return convertNumberToText(payload)
	}
	const setErrorMessage = (msg: string, override = false) => {
		return {
			message: `${!override ? 'Please enter a valid ' : ''} ${msg}`,
			required: true
		}
	}

	const validateValue = (_: unknown, value: number) => {
		return resolve(async () => {
			const val = StringToNumber(value)
			if (val < 0) {
				return Promise.reject(new Error('Vehicle value can not be empty!'))
			} else if (val < 500000) {
				return Promise.reject(new Error('Vehicle value must be minimum of 500,000!'))
			}
			return Promise.resolve()
		})
	}
	const { getVehicleMake } = useResourcesApi()
	const showVehicleMake = (arg: string | undefined) => vehicleManufacturer.value.find(item => item.id === arg)
	const isValidCharacters = /^[a-zA-Z0-9]+$/
	const setFields = async () => {
		return resolve(async () => {
			const [vehicleMakes, slotResult] = await Promise.allSettled([getVehicleMake(), props.fetchEmptySlot()])

			if (vehicleMakes.status === 'fulfilled') {
				vehicleManufacturer.value = vehicleMakes.value
			}
			if (slotResult.status === 'fulfilled') {
				const result = slotResult.value
				if (Array.isArray(result?.items) && result.items.length) {
					result.items = result.items.map(item => {
						const vehicleValue = item?.meta?.vehicle_value ?? item?.meta?.car_value ?? ''
						const staffDetails: Partial<CustodianContext> = props.uploadFromEmployees
							? {
									employee_id: {
										value: null,
										type: 'select',
										class: 'min-w-[150px]',
										searchable: true,
										options: [],
										validations: { required: true, message: 'Select a staff from the list.' }
									}
								}
							: {
									first_name: {
										value: '',
										validations: setErrorMessage('Enter your policy holder first name', true)
									},
									last_name: {
										value: '',
										validations: setErrorMessage('Enter your policy holder last name', true)
									},
									phone_number: {
										value: '',
										type: 'tel',
										validations: {
											min: 11,
											max: 11,
											...setErrorMessage('Enter your policy holder valid phone number', true)
										}
									},
									email: {
										value: '',
										validations: setErrorMessage('Enter your policy holder email address', true)
									}
								}

						let data: Partial<CustodianContext> = {
							sn: true,
							isUsed: item?.is_used || false,
							vehicle_value: {
								value: vehicleValue ? internationalNoFormat(vehicleValue, '₦') : '',
								currencyFormat: true,
								type: 'value',
								disabled: !!vehicleValue,
								validations: {
									validator: validateValue,
									required: true
								},
								inputMode: 'numeric'
							},
							manufacturer_id: {
								value: (item?.meta?.vehicle_make as DetailsType)?.name ?? null,
								type: !item?.meta?.vehicle_make ? 'select' : 'text',
								class: 'min-w-[150px]',
								searchable: true,
								disabled: !!item?.meta?.vehicle_make,
								options: vehicleManufacturer.value,
								validations: setErrorMessage('vehicle manufacturer')
							},
							temp_id: item.temp_id as string,
							vehicle_type: {
								value: item?.meta?.vehicle_type?.name ?? '',
								disabled: true
							},
							vehicle_owner_name: {
								value: User.value?.customer?.name ?? '',
								disabled: !!item?.meta?.vehicle_owner,
								validations: setErrorMessage('Specify vehicle owner', true)
							},
							plate_number: {
								value: '',
								validations: {
									min: 5,
									...setErrorMessage('plate number'),
									pattern: isValidCharacters
								}
							},
							...staffDetails,
							isActive: true,
							editable: true,
							done: false
						}
						if (categoryType.value?.isComprehensive) {
							data = {
								...data,
								vehicle_model: {
									value: (item?.meta?.vehicle_model as DetailsType)?.name ?? '',
									class: 'min-w-[150px]',
									required: true,
									disabled: true
								}
							}
						}
						return data
					})
				}
				return result
			}
			return null
		}, fetchingSlots)
	}
	const fieldValidator = async (payload: Record<string, DynamicTableRecord>, fn: Function) => {
		const vehicleValue = StringToNumber(String(payload?.vehicle_value?.value ?? 0))
		let errMsg = ''
		return resolve(async () => {
			if (!String(payload.plate_number?.value).match(isValidCharacters)) {
				errMsg = 'Plate number must contain only letters and numbers.'
			} else if (vehicleValue < MINIMUM_VEHICLE_VALUE && !categoryType.value?.isComprehensive) {
				errMsg = `Minimum vehicle value required is ${internationalNoFormat(MINIMUM_VEHICLE_VALUE, '₦')}`
			} else {
				const response = await validatePlateNumber({ plate_number: String(payload?.plate_number?.value) })
				if (response?.data.exists) {
					errMsg = 'The provided plate number is already registered.'
				}
			}

			if (errMsg) {
				fn(Promise.reject(errMsg))
				return useNotification().alert({
					message: errMsg,
					status: 400
				})
			}
			fn(Promise.resolve(true))
		})
	}
	const proceedAfterSubmission = () => {
		// @ts-ignore
		emits('done', needToKnow.value)
	}
	const submitForm = async () => {
		const payload = {
			...(props?.uploadFromEmployees ? { useEmployeeList: true } : {}),
			purchase_id: String(props.purchaseId),
			details: formToSubmit.value.map((item, i) => {
				const defaultOmits = ['done', 'editable', 'isActive', 'isUsed', 'sn', 'loading', 'key']
				let payload = item

				if (categoryType.value?.isComprehensive) {
					payload = filterObject(
						{
							...payload,
							row_number: i + 1
						},
						[
							...defaultOmits,
							'vehicle_value',
							'vehicle_type',
							'manufacturer_id',
							'vehicle_model',
							'vehicle_model'
						]
					)
				}
				payload = filterObject(
					{
						...payload,
						vehicle_value: StringToNumber(payload.vehicle_value as string),
						car_value: StringToNumber(payload.vehicle_value as string),
						row_number: i + 1
					},
					defaultOmits
				)
				return { ...payload, phone_number: replacePhoneNumberToNaija(payload.phone_number as string) }
			})
		} as AddAutoCustodianPayload

		resolve(async () => {
			const res = await addAutoCustodian(payload)
			if (res?.data) {
				useNotification().alert({
					status: 200,
					message: res?.responseText || ''
				})
				proceedAfterSubmission()
			}
		}, isSubmitting)
	}
	const { attachStaffInfo } = useStaffList()
	const { screenSizesBreak } = useMobile()
</script>

<template>
	<section>
		<forms-policy-activation-batch-upload
			v-if="urlQuery?.method === 'batch'"
			preview-img="/images/png/vehicle-custodian-upload.png"
			file-name="auto-custodian-upload-template"
			:plan-id="String(props.purchaseId)"
			:upload-steps="batchUploadGuide"
			@done="proceedAfterSubmission"
		/>
		<template v-else-if="!viewStaff">
			<uploads-add-staff-block
				v-if="!screenSizesBreak.biggerThanMd"
				:form="form"
				:data="setFields"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				:submitting="isSubmitting"
				@validator="fieldValidator"
				@update:value="$emit('update:form', $event)"
				@done="submitForm"
			/>

			<uploads-add-staff-table
				v-else
				:records="setFields"
				:table-columns="tableColumns"
				:loading="loading || fetchingSlots"
				:submitting="isSubmitting"
				:options="{ pagination: true, filter: { page: 1 } }"
				:deletable="false"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				file-name="add-staff-template"
				class="w-full"
				@update:value="e => $emit('update:form', e)"
				@validator="fieldValidator"
				@done="submitForm"
			>
				<template #employee_id="{ column, record }">
					<forms-staff-list
						v-if="record?.['employee_id'] && uploadFromEmployees"
						v-model:value="(record.employee_id as DynamicTableRecord).value"
						placeholder="Pick from employee list"
						:purchase-id="purchaseId"
						:name="String(column ?? 'staff-list')"
						@handle-change="e => attachStaffInfo(e, record as Record<string, DynamicTableRecord>)"
					/>
				</template>
				<template #filled-employee_id="{ record }">
					{{ (record?.first_name as DynamicTableRecord)?.value ?? '' }}
					{{ (record?.last_name as DynamicTableRecord)?.value ?? '+1' }}
				</template>
				<template #filled-manufacturer_id="{ value }"> {{ showVehicleMake(value)?.label ?? value }} </template>
			</uploads-add-staff-table>
		</template>

		<ui-data-table v-else :columns="tableColumns" :records="fetchEmptySlot" :loading="fetchingSlots" class="mt-16">
			<template #bodyCell="{ column: { key }, record }">
				<span v-if="key === 'employee_id'">
					{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}
					{{ record?.meta?.last_name ?? record?.last_name ?? '' }}
				</span>
				<span v-if="key === 'first_name'">{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}</span>
				<span v-if="key === 'last_name'">{{ record?.meta?.last_name ?? record?.last_name ?? 'N/A' }}</span>
				<span v-if="key === 'email'">{{ record?.meta?.email ?? record?.email ?? 'N/A' }}</span>
				<span v-if="key === 'phone_number'">{{
					record?.meta?.phone_number ?? record?.phone_number ?? 'N/A'
				}}</span>
				<span v-if="key === 'vehicle_type'">{{ record?.meta?.vehicle_type?.name ?? 'N/A' }}</span>
				<span v-if="key === 'vehicle_model'">{{ record?.meta?.vehicle_model?.name ?? 'N/A' }}</span>
				<span v-if="key === 'plate_number'">{{ record?.meta?.plate_number ?? 'N/A' }}</span>
				<span v-if="key === 'vehicle_owner_name'">{{ record?.meta?.purchase_by ?? 'N/A' }}</span>
				<span v-if="key === 'manufacturer_id'">{{ record?.meta?.vehicle_make?.name ?? 'N/A' }}</span>
				<span v-if="key === 'gadget_type'">{{ record?.meta?.gadget_type?.name ?? 'N/A' }}</span>
				<span v-if="key === 'vehicle_value'">
					{{ internationalNoFormat(record?.meta?.vehicle_value ?? 0, '₦') }}
				</span>

				<span v-if="key === 'action'">
					<button type="button" class="0 default-btn-sm mx-auto bg-primary-100 text-white" disabled>
						<lazy-tools-icon
							dir="fill"
							icon="check-2"
							:height="18"
							:width="18"
							path-class="stroke-white fill-transparent"
							class="mr-2"
						/>
						<span>Added</span>
					</button>
				</span>
			</template>
		</ui-data-table>
	</section>
</template>
